import { ChecklistBlockConfigurationRecord } from "models/view-models/block-configurations/checklist-block-configuration-record";
import { ChecklistItemRecord } from "models/view-models/block-configurations/checklist-item-record";
import PageBlockChecklistMenu from "molecules/page-block-content/page-block-checklist-menu";
import { PageBlockEditorProps } from "organisms/page-blocks/active-page-block/page-block-editor";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import ChecklistBlockConfigurationService, {
    ChecklistBlockConfigurationUpdatePathParams,
    ChecklistBlockConfigurationUpdatePutParams,
} from "utilities/services/block-configurations/checklist-block-configuration-service";
import { ChecklistDisplay } from "models/enumerations/notebooks/checklist-item-display-type";
import ChecklistItemEditor from "./checklist-item-editor";
import useSplitOrMergeChecklistItems, {
    MergeChecklistItemEvent,
} from "utilities/hooks/domain/notebooks/pages/checklists/use-split-or-merge-checklist-items";
import DeprecatedShow from "atoms/wrappers/show";
import { RecordUtils } from "@rsm-hcd/javascript-core";
import {
    AdditionalChecklistBlockData,
    DEFAULT_ADDITIONAL_CHECKLIST_BLOCK_DATA,
    isChecklistAdditionalData,
} from "models/interfaces/additional-block-data/additional-checklist-block-data";
import { CollectionUtils } from "utilities/collection-utils";
import useChecklistItem from "utilities/hooks/domain/notebooks/pages/checklists/use-checklist-item";
import {
    DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_NAME,
    SharedSortableDragAndDropList,
    SortableDragAndDropScopeConfiguration,
} from "organisms/sortable-drap-and-drop/shared-sortable-drag-and-drop-list";
import SortableDragAndDropItem from "organisms/sortable-drap-and-drop/sortable-drag-and-drop-item";
import { closestCenter, CollisionDetection } from "@dnd-kit/core";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization-utils";
import useMostRecentlyEditedOn from "utilities/hooks/domain/notebooks/pages/use-most-recently-edited-on";
import useCurrentPage from "utilities/hooks/domain/notebooks/pages/use-current-page";

type ItemUpdater =
    | ChecklistItemRecord[]
    | ((prevItems: ChecklistItemRecord[]) => ChecklistItemRecord[]);

interface ChecklistBlockEditorProps extends PageBlockEditorProps {}

export const CHECKLIST_ITEM_DRAG_AND_DROP_SCOPE = `${DEFAULT_SORTABLE_DRAG_AND_DROP_SCOPE_NAME}-checklist-items`;
const CSS_CLASS_NAME = "c-checklist-block-editor";

const ChecklistBlockEditor = ({
    additionalBlockData,
    block,
    onBlockModified,
    onBlockSaved,
    onBlockSaving,
    onDelete,
    onDone,
    onMoveDown,
    onMoveUp,
}: ChecklistBlockEditorProps) => {
    const { createChecklistItem } = useChecklistItem();
    const { update: updateConfigurationApi } =
        ChecklistBlockConfigurationService.useUpdate();
    const hasCreatedFirstChecklistItem = useRef<boolean>(false);

    const checklistBlockConfiguration = useMemo(() => {
        const configuration = RecordUtils.ensureRecord(
            block.configuration,
            ChecklistBlockConfigurationRecord
        );

        return configuration;
    }, [block.configuration]);
    const { currentPage } = useCurrentPage();
    const { refreshMostRecentlyEditedOn } = useMostRecentlyEditedOn(
        currentPage?.id
    );

    const {
        isLoadingItems,
        items,
        onItemsReordered,
        updateItems: updateItemsInAdditionalBlockData,
    } = useMemo((): AdditionalChecklistBlockData => {
        if (!isChecklistAdditionalData(additionalBlockData)) {
            return DEFAULT_ADDITIONAL_CHECKLIST_BLOCK_DATA;
        }

        return additionalBlockData;
    }, [additionalBlockData]);

    const handleOnMergeBlocked = () => {
        ToastManager.info(t("checklist-merging-blocked-info"));
        return;
    };

    const updateChecklistItems = useCallback(
        (updateItems: ItemUpdater): void => {
            const updatedItems =
                typeof updateItems === "function"
                    ? updateItems(items)
                    : updateItems;

            updateItemsInAdditionalBlockData(updatedItems);
        },
        [items, updateItemsInAdditionalBlockData]
    );

    const updateItemInChecklist = (updatedItem: ChecklistItemRecord): void => {
        updateChecklistItems((previousItems) => {
            return buildUpdatedChecklistItemList(updatedItem, previousItems);
        });
    };

    const {
        handleMergeCurrentWithNext,
        handleMergeCurrentWithPrevious,
        handleSplitContentBetweenItems,
        saveInProgress,
    } = useSplitOrMergeChecklistItems(
        checklistBlockConfiguration.id ?? 0,
        items,
        onBlockSaving,
        onBlockSaved,
        updateChecklistItems,
        checklistBlockConfiguration,
        handleOnMergeBlocked
    );

    async function handleToggleConfigurationProperty(
        property: ChecklistDisplay
    ): Promise<void> {
        if (checklistBlockConfiguration?.id == null) {
            console.warn("No checklist configuration to save");
            return;
        }

        try {
            onBlockSaving();

            const requestData: ChecklistBlockConfigurationUpdatePutParams = {
                isNumbered: checklistBlockConfiguration.isNumbered,
                usesCustomStatuses:
                    checklistBlockConfiguration.usesCustomStatuses,
            };

            if (property === ChecklistDisplay.Numbered) {
                requestData.isNumbered = !requestData.isNumbered;
            } else if (property === ChecklistDisplay.Status) {
                requestData.usesCustomStatuses =
                    !requestData.usesCustomStatuses;
            }

            const pathParams: ChecklistBlockConfigurationUpdatePathParams = {
                configurationId: checklistBlockConfiguration.id,
            };

            const response = await updateConfigurationApi(
                requestData,
                pathParams
            );

            if (response?.result == null || response.resultObject == null) {
                throw new Error(
                    "No response provided when updating checklist configuration"
                );
            }

            if (response.result.hasErrors()) {
                throw new Error(response.result.listErrorMessages().join(", "));
            }

            const updatedConfiguration = response.resultObject.with({
                items: items,
            });
            const updatedBlock = block.with({
                configuration: updatedConfiguration,
            });

            onBlockSaved(updatedBlock);
        } catch (error) {
            console.error(error);

            onBlockSaved(block);
        }
    }

    const handleItemModified = (updatedItem: ChecklistItemRecord): void => {
        updateItemInChecklist(updatedItem);

        onBlockModified(block);
    };

    const handleItemSaved = (updatedItem: ChecklistItemRecord): void => {
        updateItemInChecklist(updatedItem);

        const updatedBlock = block.with({
            configuration: checklistBlockConfiguration.with({
                items: buildUpdatedChecklistItemList(updatedItem, items),
            }),
        });
        onBlockSaved(updatedBlock);
    };

    const handleItemSaving = (): void => {
        onBlockSaving();
    };

    const handleSplitChecklistItem = useCallback(
        async (index: number, cursorPosition: number) => {
            await handleSplitContentBetweenItems(index, cursorPosition);
        },
        [handleSplitContentBetweenItems]
    );

    const handleOnChecklistItemMove = async (
        reorderedChecklistItems: ChecklistItemRecord[]
    ) => {
        await onItemsReordered(reorderedChecklistItems);
        const itemsAsRecords = RecordUtils.ensureRecords(
            reorderedChecklistItems,
            ChecklistItemRecord
        );
        await refreshMostRecentlyEditedOn();

        updateChecklistItems(itemsAsRecords);
    };

    const handleMergeChecklistItem = useCallback(
        async (event: MergeChecklistItemEvent, index: number) => {
            switch (event) {
                case MergeChecklistItemEvent.MergeWithNext:
                    await handleMergeCurrentWithNext(index);
                    break;
                case MergeChecklistItemEvent.MergeWithPrevious:
                    await handleMergeCurrentWithPrevious(index);
                    break;
                default:
                    console.warn(`Unknown event type: ${event}`);
                    break;
            }
            await refreshMostRecentlyEditedOn();
        },
        [
            handleMergeCurrentWithNext,
            handleMergeCurrentWithPrevious,
            refreshMostRecentlyEditedOn,
        ]
    );

    const handleDone = async () => {
        // await handleToggleConfigurationProperty();
        await refreshMostRecentlyEditedOn();
        onDone?.();
    };

    const handleDelete = async () => {
        await refreshMostRecentlyEditedOn();
        await onDelete(block);
    };

    const handleMoveUp = async () => {
        await refreshMostRecentlyEditedOn();
        await onMoveUp(block);
    };

    const handleMoveDown = async () => {
        await refreshMostRecentlyEditedOn();
        await onMoveDown(block);
    };

    const createFirstChecklistItem = useCallback(async () => {
        if (
            hasCreatedFirstChecklistItem.current ||
            checklistBlockConfiguration?.id == null ||
            checklistBlockConfiguration.id <= 0
        ) {
            return;
        }

        hasCreatedFirstChecklistItem.current = true;

        try {
            const createdItem = await createChecklistItem(
                checklistBlockConfiguration.id!
            );

            if (createdItem == null) {
                throw new Error();
            }

            updateChecklistItems([createdItem]);
        } catch {
            console.error("Failed to create a new checklist item");
        }
    }, [
        checklistBlockConfiguration.id,
        createChecklistItem,
        updateChecklistItems,
    ]);

    useEffect(() => {
        const shouldNotCreateFirstChecklistItem =
            isLoadingItems ||
            hasCreatedFirstChecklistItem.current ||
            checklistBlockConfiguration?.id == null ||
            checklistBlockConfiguration.id <= 0 ||
            CollectionUtils.hasValues(items);

        if (shouldNotCreateFirstChecklistItem) {
            return;
        }

        createFirstChecklistItem();
    }, [
        checklistBlockConfiguration.id,
        createFirstChecklistItem,
        isLoadingItems,
        items,
    ]);

    return (
        <div className={CSS_CLASS_NAME}>
            <PageBlockChecklistMenu
                isNumbered={checklistBlockConfiguration.isNumbered}
                showStatus={checklistBlockConfiguration.usesCustomStatuses}
                onToggleNumbering={() => {
                    handleToggleConfigurationProperty(
                        ChecklistDisplay.Numbered
                    );
                }}
                onToggleStatus={() => {
                    handleToggleConfigurationProperty(ChecklistDisplay.Status);
                }}
                onDone={handleDone}
                onDelete={handleDelete}
                onMoveDown={handleMoveDown}
                onMoveUp={handleMoveUp}
            />
            <DeprecatedShow when={checklistBlockConfiguration.id != null}>
                <SharedSortableDragAndDropList
                    dragAndDropScopeName={CHECKLIST_ITEM_DRAG_AND_DROP_SCOPE}
                    dragAndDropScopeConfiguration={
                        dragAndDropScopeConfiguration
                    }
                    items={items}
                    onSortChange={handleOnChecklistItemMove}
                    strategy={verticalListSortingStrategy}>
                    {items.map((item, index) => (
                        <SortableDragAndDropItem
                            allowIconOnlyDrag
                            dragAndDropScopeName={
                                CHECKLIST_ITEM_DRAG_AND_DROP_SCOPE
                            }
                            id={item.id!}
                            key={item.id!}>
                            <React.Fragment key={item.id}>
                                <ChecklistItemEditor
                                    index={index}
                                    isNumbered={
                                        checklistBlockConfiguration.isNumbered
                                    }
                                    item={item}
                                    key={item.id}
                                    onItemModified={handleItemModified}
                                    onItemSaved={handleItemSaved}
                                    onItemSaving={handleItemSaving}
                                    onMergeChecklistItem={
                                        handleMergeChecklistItem
                                    }
                                    onSplitChecklistItem={
                                        handleSplitChecklistItem
                                    }
                                    showStatus={
                                        checklistBlockConfiguration.usesCustomStatuses
                                    }
                                    totalItemCount={items.length}
                                    saveInProgress={saveInProgress}
                                />
                            </React.Fragment>
                        </SortableDragAndDropItem>
                    ))}
                </SharedSortableDragAndDropList>
            </DeprecatedShow>
        </div>
    );
};

const checklistItemCollisionDetection: CollisionDetection = (args) => {
    const topOffset = 10;
    const pointerCenteredCollisionRect = {
        ...args.collisionRect,
        left: args.pointerCoordinates?.x ?? args.collisionRect.left,
        top: (args.pointerCoordinates?.y ?? args.collisionRect.top) + topOffset,
    };

    return closestCenter({
        ...args,
        collisionRect: pointerCenteredCollisionRect,
    });
};

const dragAndDropScopeConfiguration: SortableDragAndDropScopeConfiguration = {
    collisionDetection: checklistItemCollisionDetection,
    contextModifiers: [],
};

const buildUpdatedChecklistItemList = (
    updatedItem: ChecklistItemRecord,
    previousItemsIncludingNulls: ChecklistItemRecord[]
) => {
    const previousItems = previousItemsIncludingNulls.filter(
        (item) => item.id != null
    );

    const itemToUpdateIndex = previousItems.findIndex(
        (item) => item.id === updatedItem.id
    );

    if (itemToUpdateIndex === -1) {
        return [...previousItems, updatedItem];
    }

    const itemToUpdate = previousItems[itemToUpdateIndex];
    const updatedItemWithNote = updatedItem.with({
        note: updatedItem.note ?? itemToUpdate.note,
    });

    return previousItems.toSpliced(itemToUpdateIndex, 1, updatedItemWithNote);
};

export default ChecklistBlockEditor;
